import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from '@mui/joy';
import { Paper, Typography as MuiTypography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { getAllSettings, updateSettingById } from '../../../main/API/settings';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';

/**
 * Controls the five `WHATSAPP_NOTIFY_ON_<STATUS>` Setting flags that gate the
 * automatic WhatsApp dispatcher. Each flag is stored as a `Setting` row whose
 * `value` is the string `"true"` or `"false"`. When OFF, automatic messages
 * for that status are paused; operators can still force-send from the scan
 * screen via the per-scan override toggle. See
 * FRONTEND_AUTOMATIC_WHATSAPP_NOTIFICATIONS_GUIDE §2.
 */
const FLAGS = [
  { name: 'WHATSAPP_NOTIFY_ON_SHIPPING_SOON', labelKey: 'whatsappNotify.statusShippingSoon', fallback: 'Shipping soon' },
  { name: 'WHATSAPP_NOTIFY_ON_SHIPPED', labelKey: 'whatsappNotify.statusShipped', fallback: 'Shipped' },
  { name: 'WHATSAPP_NOTIFY_ON_ARRIVED', labelKey: 'whatsappNotify.statusArrived', fallback: 'Arrived' },
  { name: 'WHATSAPP_NOTIFY_ON_OUT_FOR_DELIVERY', labelKey: 'whatsappNotify.statusOutForDelivery', fallback: 'Out for delivery' },
  { name: 'WHATSAPP_NOTIFY_ON_DELIVERED', labelKey: 'whatsappNotify.statusDelivered', fallback: 'Delivered' },
];

const truthy = (val) => String(val ?? '').trim().toLowerCase() === 'true';

export default function WhatsappNotifyFlagsSetting() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canList = hasPermission(PERMISSIONS.SETTINGS_LIST);
  const canUpdate = hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL);

  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState(null);
  const [rows, setRows] = useState({});
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canList) return;
    setLoading(true);
    setError(null);
    try {
      const settings = await getAllSettings();
      const list = Array.isArray(settings) ? settings : [];
      const next = {};
      FLAGS.forEach(({ name }) => {
        next[name] = list.find((s) => s && s.name === name) || null;
      });
      setRows(next);
    } catch (e) {
      setError(translateAxiosErrorMessage(e, t) || t('whatsappNotify.loadFailed', { defaultValue: 'Failed to load notification settings' }));
    } finally {
      setLoading(false);
    }
  }, [canList, t]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = useCallback(async (name, nextChecked) => {
    const setting = rows[name];
    if (!canUpdate || !setting) return;
    setSavingName(name);
    setError(null);
    const nextValue = nextChecked ? 'true' : 'false';
    try {
      await updateSettingById(setting.id, {
        name: setting.name,
        value: nextValue,
        description: setting.description || '',
      });
      setRows((prev) => ({ ...prev, [name]: { ...setting, value: nextValue } }));
      setSnack({ open: true, color: 'success', message: t('whatsappNotify.saved', { defaultValue: 'Notification setting updated.' }) });
    } catch (e) {
      setSnack({
        open: true,
        color: 'danger',
        message: translateAxiosErrorMessage(e, t) || t('whatsappNotify.saveFailed', { defaultValue: 'Could not save notification setting.' }),
      });
    } finally {
      setSavingName(null);
    }
  }, [rows, canUpdate, t]);

  return (
    <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#ECFDF5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 24 }} />
              </Box>
              <Box>
                <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {t('whatsappNotify.title', { defaultValue: 'WhatsApp auto-notifications' })}
                </MuiTypography>
                <MuiTypography variant="body2" color="#6B7280">
                  {t('whatsappNotify.subtitle', { defaultValue: 'Toggle which shipment milestones automatically message the customer via WhatsApp.' })}
                </MuiTypography>
              </Box>
            </Box>
            <Divider />
          </Box>

          <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
            {t('whatsappNotify.notice', {
              defaultValue: 'When OFF, automatic messages for this status are paused. Operators can still force-send WhatsApp from the scan screen.',
            })}
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {FLAGS.map(({ name, labelKey, fallback }) => {
                const setting = rows[name];
                const checked = setting ? truthy(setting.value) : false;
                const busy = savingName === name;
                return (
                  <Grid item xs={12} md={6} key={name}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #F3F4F6',
                        background: '#FFFFFF',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.25 }}>
                          {t(labelKey, { defaultValue: fallback })}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'monospace' }}>
                          {name}
                        </Typography>
                        {!setting && (
                          <Typography level="body-xs" sx={{ color: 'warning.700', mt: 0.5 }}>
                            {t('whatsappNotify.missingFlag', {
                              defaultValue: 'Setting "{{name}}" was not found. Run backend migrations to seed defaults.',
                              name,
                            })}
                          </Typography>
                        )}
                      </Box>
                      {busy ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <Switch
                          checked={checked}
                          onChange={(e) => handleToggle(name, e.target.checked)}
                          disabled={!setting || !canUpdate}
                          color={checked ? 'success' : 'neutral'}
                        />
                      )}
                    </Paper>
                  </Grid>
                );
              })}
              {error && (
                <Grid item xs={12}>
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Paper>

      <Snackbar
        autoHideDuration={3000}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </PermissionGate>
  );
}
